import Link from 'next/link';
import {
  isReferenceObject,
  OpenAPIObject,
  OperationObject,
  ParameterObject,
  SchemaObject,
} from 'openapi3-ts';
import snarkdown from 'snarkdown';

import { BaseProps, SchemaObjectEntry } from '.';
import {
  getContentProperty,
  getFormattedExample,
  getRouteFromDefinition,
  getSchemaNamePrefix,
} from './schemaUtils';
import { replaceApiRouteLinks, snarkdownWithParagraphs } from '../utilities';
import CodeBlockV2 from '../CodeBlockV2';
import SchemaLayout from './SchemaLayout/SchemaLayout';
import { normalizeSchemaForRendering } from './SchemaLayout/schemaUtils';

import styles from './index.module.scss';

interface RequestSchemaProps extends BaseProps {
  definition: OpenAPIObject;
  route: string;
  method: string;
  simplifyForBill: boolean;
}

// In CoreExchange, it looks like the "parameters" property includes both
// path parameters and query parameters.
const getRequestParameters = (fullRouteObject: OperationObject) => {
  // Is it weird the whole point of this function is to just error out if
  // a reference object is passed in?
  const allParameters = fullRouteObject?.parameters || [];
  const filteredParams: ParameterObject[] = [];
  allParameters.forEach((param) => {
    if (isReferenceObject(param)) {
      throw new Error('Schema: model was not a ParametersObject');
    } else {
      filteredParams.push(param);
    }
  });
  return filteredParams;
};

// I'm very confused here. I don't think anybody uses this.
// Plaid-dot-com has requestBody objects, but no examples in the API spec.
// Core exchange has no requestBody objects.

// I'm going to comment this out and see if anything breaks.
/* const fetchExamples = (
  fullRouteObject: OperationObject,
  exampleIndex: number,
) => {
  console.log(
    'Request body for route',
    fullRouteObject.description,
    fullRouteObject.requestBody,
  );

  if (fullRouteObject.requestBody == null) {
    return '';
  }
  const maybeRequestExamples: SchemaObject['examples'] = getContentProperty(
    !isReferenceObject(fullRouteObject?.requestBody)
      ? fullRouteObject?.requestBody?.content
      : undefined,
    'examples',
  );
  const exampleRequestCode =
    maybeRequestExamples != null
      ? getFormattedExample(maybeRequestExamples, exampleIndex)
      : '';

  console.log('exampleRequestCode', exampleRequestCode);

  return exampleRequestCode;
}; */

const RequestSchema: React.FC<RequestSchemaProps> = (props) => {
  const fullRouteObject = getRouteFromDefinition(
    props.definition,
    props.route,
    props.method,
  );
  if (fullRouteObject == null) {
    return null;
  }

  const routeKebabCase = getSchemaNamePrefix(props.route, props.method);

  const pathParamEntries = getRequestParameters(fullRouteObject);

  let requestSchemaObject: SchemaObject;

  // Docs have request bodies, code exchange docs don't.
  if (fullRouteObject.requestBody == null) {
    requestSchemaObject = {};
  } else {
    requestSchemaObject = getContentProperty(
      !isReferenceObject(fullRouteObject.requestBody)
        ? fullRouteObject.requestBody?.content
        : undefined,
      'schema',
    );
  }

  if (requestSchemaObject != null) {
    requestSchemaObject = normalizeSchemaForRendering(requestSchemaObject);
  }
  const requestEntries: Array<SchemaObjectEntry> = Object.entries(
    requestSchemaObject?.properties || {},
  );

  const required: Array<string> =
    fullRouteObject.requestBody != null
      ? requestSchemaObject.required || []
      : [];

  // const exampleRequestResponse = fetchExamples(
  //   fullRouteObject,
  //   props.exampleIndex,
  // );

  const exampleRequestResponse = '';

  const id = fullRouteObject.summary.toLowerCase().split(' ').join('-');

  return (
    <SchemaLayout
      route={props.route}
      schemaName={`${routeKebabCase}-request`}
      tableOnly={props.tableOnly}
      expandAll={!props.collapse}
      expandText={props.expandText}
      hideText={props.hideText}
      unhideFlag={props.unhideFlag}
      entries={requestEntries}
      schema={requestSchemaObject}
      pathEntries={pathParamEntries}
      simplify={props.simplifyForBill}
      isRequest={true}
      type={'request'}
      description={
        <>
          <Link href={`#${id}`}>
            <h4 className={styles.schemaHeader}>
              {fullRouteObject.summary}
              <span
                id={id}
                className={styles.offsetAnchor}
                data-algolia='linkedHeadingAnchor'
              />
            </h4>
          </Link>

          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: snarkdownWithParagraphs(
                snarkdown,
                replaceApiRouteLinks(
                  fullRouteObject.description,
                  props.definition,
                ),
              ),
            }}
          />
        </>
      }
      headerText='Request fields'
      required={required}
    >
      {props.children != null ? (
        <div className={styles.codeBlock}>{props.children}</div>
      ) : exampleRequestResponse !== '' ? ( // If no example and no children, Exchange will use the Layout.SideBySide component in the MDX file to render hard coded bash example.
        <CodeBlockV2
          className={styles.codeBlock}
          code={exampleRequestResponse}
          lang='json'
          title={props.exampleTitle != null ? props.exampleTitle : 'API Object'}
        />
      ) : null}
    </SchemaLayout>
  );
};

export default RequestSchema;
